import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { getAthletes } from "api-client";
import { Icon } from "icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  ButtonGroup,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "ui";
import { useDebounce } from "use-debounce";

const PAGE_SIZE = 20;
const ATHLETE_SECTIONS = new Set([
  "calendar",
  "metrics",
  "profile",
  "programs",
  "questionnaires",
  "statistics",
]);

interface AthleteBreadcrumbSwitcherProps {
  athleteHref: string;
  athleteId: string;
  athleteName: string;
  isCurrentPage: boolean;
  pathname: string;
}

function getAthleteHref(pathname: string, athleteId: string) {
  const section = pathname.match(/^\/athletes\/[^/]+\/([^/]+)/)?.[1];
  const targetSection =
    section && ATHLETE_SECTIONS.has(section) ? section : "profile";

  return `/athletes/${athleteId}/${targetSection}`;
}

export function AthleteBreadcrumbSwitcher(
  props: AthleteBreadcrumbSwitcherProps,
) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchPhrase, setSearchPhrase] = useState("");
  const [debouncedSearchPhrase] = useDebounce(searchPhrase, 200);

  const athletesQuery = useInfiniteQuery({
    queryKey: ["athletes", "breadcrumb-switcher", debouncedSearchPhrase],
    queryFn: async ({ pageParam }) => {
      const response = await getAthletes({
        query: {
          pageIndex: pageParam,
          pageSize: PAGE_SIZE,
          query: debouncedSearchPhrase,
        },
      });

      return response.data.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (!lastPage.pageInfo.hasNextPage) {
        return undefined;
      }

      return lastPage.pageInfo.pageIndex + 1;
    },
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const athletes =
    athletesQuery.data?.pages.flatMap((page) => {
      return page.currentPage.map((athlete) => {
        return {
          id: athlete.id,
          name: athlete.user.name,
        };
      });
    }) ?? [];

  const options = athletes.filter((athlete) => {
    return athlete.id !== props.athleteId;
  });

  if (searchPhrase.trim() === "") {
    options.unshift({
      id: props.athleteId,
      name: props.athleteName,
    });
  } else {
    const currentAthlete = athletes.find((athlete) => {
      return athlete.id === props.athleteId;
    });

    if (currentAthlete) {
      options.unshift(currentAthlete);
    }
  }

  const isEmpty = !athletesQuery.isFetching && options.length === 0;

  function tryFetchNextPage() {
    if (athletesQuery.isFetchingNextPage || !athletesQuery.hasNextPage) {
      return;
    }

    void athletesQuery.fetchNextPage();
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);

    if (!nextOpen) {
      setSearchPhrase("");
    }
  }

  function handleSelect(athleteId: string) {
    setOpen(false);
    setSearchPhrase("");
    void navigate({
      to: getAthleteHref(props.pathname, athleteId),
    });
  }

  return (
    <Popover modal open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <ButtonGroup className="max-w-64">
          <Button
            asChild
            variant="ghost"
            size="sm"
            className={cn(
              "min-w-0 font-normal",
              props.isCurrentPage && "text-foreground",
            )}
          >
            <Link
              to={props.athleteHref}
              aria-current={props.isCurrentPage ? "page" : undefined}
            >
              <span className="truncate">{props.athleteName}</span>
            </Link>
          </Button>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              role="combobox"
              aria-expanded={open}
              aria-label={t("selectAthlete.switch")}
            >
              <Icon name="tabler-chevron-down" aria-hidden />
            </Button>
          </PopoverTrigger>
        </ButtonGroup>
      </PopoverAnchor>
      {open ? (
        <PopoverContent align="start" className="w-64 p-0">
          <Command
            shouldFilter={false}
            onItemHighlighted={(itemId) => {
              if (itemId === options[options.length - 1]?.id) {
                tryFetchNextPage();
              }
            }}
          >
            <CommandInput
              autoFocus
              placeholder={t("selectAthlete.searchPlaceholder")}
              value={searchPhrase}
              onValueChange={setSearchPhrase}
              left={
                athletesQuery.isFetching ? (
                  <Icon
                    name="tabler-loader-2"
                    className="mr-2 size-4 shrink-0 animate-spin opacity-50"
                    aria-hidden
                  />
                ) : undefined
              }
            />
            <CommandList
              onScroll={(event) => {
                const list = event.currentTarget;
                const isAtBottom =
                  Math.ceil(list.scrollTop + list.clientHeight) >=
                  list.scrollHeight;

                if (isAtBottom) {
                  tryFetchNextPage();
                }
              }}
            >
              {isEmpty ? (
                <CommandEmpty>{t("selectAthlete.noResults")}</CommandEmpty>
              ) : (
                <CommandGroup>
                  {options.map((athlete) => {
                    const isCurrent = athlete.id === props.athleteId;

                    return (
                      <CommandItem
                        key={athlete.id}
                        value={athlete.id}
                        onSelect={() => {
                          handleSelect(athlete.id);
                        }}
                      >
                        <span className="flex-1 truncate">{athlete.name}</span>
                        <Icon
                          name="tabler-check"
                          className={cn(
                            "ml-auto size-4",
                            isCurrent ? "opacity-100" : "opacity-0",
                          )}
                          aria-hidden
                        />
                        {isCurrent ? (
                          <span className="sr-only">
                            {t("selectAthlete.current")}
                          </span>
                        ) : null}
                      </CommandItem>
                    );
                  })}
                  {athletesQuery.isFetchingNextPage ? (
                    <div className="flex justify-center p-2">
                      <Icon
                        name="tabler-loader-2"
                        className="size-4 animate-spin"
                        aria-hidden
                      />
                    </div>
                  ) : null}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      ) : null}
    </Popover>
  );
}
